import React, { useEffect, useCallback, useRef } from 'react';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { css } from '@emotion/react';

import { useSelector } from 'react-redux';
import { getNovoAIProperties } from 'redux/selectors/lecture-page';
import { NovoAIItemType } from 'redux/schemas/models/lecture-page';
import { getAIGeneratedActivity,
  getAiSuggestSummaryAndTakeaway,
  getCourseExercises,
  setAiSuggestComponentsLoaded,
  setAiSuggestComponentsLoading,
} from 'redux/actions/lecture-pages';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { wrapThunkAction } from 'redux/utils';
import { CourseExercise } from 'redux/schemas/models/exercise';
import { Result } from 'redux/schemas/api';

import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { shortBar } from 'styles/global_defaults/scaffolding';
import AiGeneratingLoader from './ai-generating-loader';
import AiGeneratedPreview from './ai-generated-preview';
import { lhsCommonStyles } from '../suggest-activities/styles';
import { getContentScopePayloadValue } from '../../utils';

const styles = css`
  padding-left: ${shortBar}px;
  padding-right: ${shortBar}px;
  ${lhsCommonStyles}
`;

interface AbortableRequest {
  abort(): void;
}

const AiGeneratingModalContent = (props: { closeModal(): void }) => {
  const { closeModal } = props;
  const novoAIProperties = useSelector(getNovoAIProperties);
  const { isLoaded, isLoading } = useSelector(state => state.app.aiSuggestComponent);

  const {
    content,
    itemType,
    lengthText,
    tone,
    itemTrueType,
  } = novoAIProperties;

  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const requestRef = useRef<AbortableRequest[]>([]);
  const isMountedRef = useRef(true);

  const getLoadingDescription = useCallback(() => {
    if ([NovoAIItemType.POST_SURVEY, NovoAIItemType.PRE_SURVEY].includes(itemType)) {
      return t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.SINGLE_SUGGEST_SURVEY_DESCRIPTION();
    }
    return t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.SINGLE_SUGGEST_DESCRIPTION();
  }, [itemType]);

  const getType = useCallback(() => {
    switch (itemTrueType) {
      case ComponentType.TEXT_WITH_IMAGE_TOP:
        return 'blurb_top_image';
      case ComponentType.TEXT_WITH_IMAGE_BKG:
        return 'blurb_background_image';
      case ComponentType.TEXT_WITH_IMAGE_SIDE:
        return 'blurb_side_image';
      default:
        return 'rich_text';
    }
  }, [itemTrueType]);

  const showErrorAlertAndCloseModal = useCallback((errorCode) => {
    let errorHeader = t.LECTURE_PAGES.LHS.NOVO_AI.ALERT.ERROR.TITLE();
    let errorMessage = '';

    if (['content_too_short', 'blank_text_extract'].includes(errorCode)) {
      errorMessage = t.LECTURE_PAGES.COMPONENTS.DISCUSSION.GENERATED_MESSAGES.TOO_SHORT();
    } else if (errorCode === 'content_too_long') {
      errorMessage = t.LECTURE_PAGES.COMPONENTS.DISCUSSION.GENERATED_MESSAGES.TOO_LONG();
    } else {
      errorHeader = t.FORM.OOPS();
      errorMessage = t.FORM.ERROR_SOMETHING_WRONG_SHORT();
    }

    dispatch(
      addAlertMessage({
        type: AlertMessageType.ERROR,
        header: errorHeader,
        message: errorMessage,
      }),
    );
    closeModal();
  }, [closeModal, dispatch]);

  const hasRequestedRef = useRef(false);

  const handleSingleAiRequest = useCallback((request) => {
    wrapThunkAction(request)
      .then(() => {
        if (!isMountedRef.current) {
          return;
        }
        dispatch(setAiSuggestComponentsLoaded(true));
      })
      .catch((error) => {
        if (!isMountedRef.current) {
          return;
        }
        if (error?.error?.name !== 'AbortError') {
          showErrorAlertAndCloseModal(error.payload?.code);
        }
      })
      .finally(() => {
        if (!isMountedRef.current) {
          return;
        }
        dispatch(setAiSuggestComponentsLoading(false));
      });
  }, [dispatch, showErrorAlertAndCloseModal]);

  const getActivities = useCallback(() => {
    hasRequestedRef.current = true;

    dispatch(setAiSuggestComponentsLoading(true));
    // Clear previous requests
    requestRef.current = [];

    if ([NovoAIItemType.SUMMARY, NovoAIItemType.KEY_TAKEAWAYS].includes(itemType)) {
      const request = dispatch(getAiSuggestSummaryAndTakeaway({
        catalogId: params.catalogId,
        lecturePageId: params.lecturePageId,
        activityType: itemType,
        contentOptions: {
          scope: getContentScopePayloadValue(content?.type),
          type: getType(),
          tone,
          length: lengthText,
        },
      }));

      requestRef.current.push(request);
      handleSingleAiRequest(request);
    } else if ([
      NovoAIItemType.POLL,
      NovoAIItemType.ASSIGNMENT,
      NovoAIItemType.PRE_SURVEY,
      NovoAIItemType.POST_SURVEY,
    ].includes(itemType)) {
      const request = dispatch(getAIGeneratedActivity({
        activityType: itemType,
      }));

      requestRef.current.push(request);
      handleSingleAiRequest(request);
    } else if (itemType === NovoAIItemType.PUBLIC_FEEDBACK) {
      const request = dispatch(getCourseExercises({
        catalogId: params.catalogId,
        withoutFeedback: true,
        scopeId: content?.id,
        scopeType: getContentScopePayloadValue(content?.type, true),
      }));

      requestRef.current.push(request);

      wrapThunkAction(request).then((res) => {
        if (!isMountedRef.current) {
          return;
        }
        // Requesting the assignments that are eligible for adding feedback
        // within the selected content.
        const assignments = (res.payload as Result<CourseExercise[]>).result;

        // Close the modal and display an alert message if there are no assignments
        if (assignments.length === 0) {
          dispatch(addAlertMessage({
            type: AlertMessageType.INFO,
            header: t.LECTURE_PAGES.LHS.NOVO_AI.ALERT.NO_ASSIGNMENT.HEADER(),
            message: t.LECTURE_PAGES.LHS.NOVO_AI.ALERT.NO_ASSIGNMENT.CONTENT(),
          }));
          closeModal();
        } else {
          if (!isMountedRef.current) {
            return;
          }
          const requests = assignments.map((assignment) => {
            const thunkPromise = dispatch(getAIGeneratedActivity({
              activityType: itemType,
              exerciseId: assignment.id,
            }));

            requestRef.current.push(thunkPromise);

            return wrapThunkAction(thunkPromise).catch((error) => {
              if (error?.error?.name !== 'AbortError') {
                showErrorAlertAndCloseModal(error.payload?.code);
              }
            });
          });

          Promise.all(requests).finally(() => {
            if (!isMountedRef.current) {
              return;
            }
            dispatch(setAiSuggestComponentsLoaded(true));
            dispatch(setAiSuggestComponentsLoading(false));
          });
        }
      }).catch((error) => {
        if (!isMountedRef.current) {
          return;
        }
        if (error?.error?.name === 'AbortError') {
          return;
        }
        dispatch(setAiSuggestComponentsLoading(false));
        showErrorAlertAndCloseModal(error.payload?.code);
      });
    }
  }, [
    closeModal,
    content?.id,
    content?.type,
    dispatch,
    getType,
    handleSingleAiRequest,
    itemType,
    lengthText,
    params.catalogId,
    params.lecturePageId,
    showErrorAlertAndCloseModal,
    tone,
  ]);

  useEffect(() => {
    if (!isLoaded && !isLoading && !hasRequestedRef.current) {
      getActivities();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup effect: abort all in-flight requests only when the modal unmounts
  useEffect(() => () => {
    isMountedRef.current = false;
    requestRef.current.forEach((request) => {
      if (request && typeof request.abort === 'function') {
        request.abort();
      }
    });
    requestRef.current = [];
  }, []);

  return (
    <div css={styles}>
      {isLoaded ? (
        <AiGeneratedPreview closeModal={closeModal} />
      ) : (
        <AiGeneratingLoader
          customDescription={getLoadingDescription()}
        />
      )}
    </div>
  );
};

export default AiGeneratingModalContent;
